import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppointmentsStore } from '../../appointments/store/appointmentsStore';
import { useHomepageStore } from '../../homepage/store/homepageStore';
import { AppBar, FadeInAnimation, ShimmerAnimation } from '../../../core/components/ui';

interface Achievement {
  id: string;
  title: string;
  description: string;
  icon: string;
  unlocked: boolean;
}

const AchievementPage: React.FC = () => {
  const navigate = useNavigate();
  const appStore = useAppointmentsStore();
  const homeStore = useHomepageStore();
  const [sessions, setSessions] = useState<any[]>([]);
  const [credits, setCredits] = useState(0);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    load();
  }, []);

  const load = async () => {
    try {
      setLoading(true);
  
      const [s, c] = await Promise.all([
        appStore.fetch(),
        homeStore.getSessionCredit(),
      ]);
  
      setSessions(s);
      setCredits(c.quantity || 0);
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const completedCount = sessions.filter(s => s.status === 'COMPLETED').length;

  const achievements: Achievement[] = [
    {
      id: 'first_session',
      title: 'First Step',
      description: 'Complete your first therapy session.',
      icon: '🌱',
      unlocked: completedCount >= 1,
    },
    {
      id: 'three_sessions',
      title: 'Committed',
      description: 'Complete 3 therapy sessions.',
      icon: '💪',
      unlocked: completedCount >= 3,
    },
    {
      id: 'five_sessions',
      title: 'Dedicated',
      description: 'Complete 5 therapy sessions.',
      icon: '⭐',
      unlocked: completedCount >= 5,
    },
    {
      id: 'ten_sessions',
      title: 'Champion',
      description: 'Complete 10 therapy sessions.',
      icon: '🏆',
      unlocked: completedCount >= 10,
    },
    {
      id: 'bought_credits',
      title: 'Investor',
      description: 'Purchase session credits for the first time.',
      icon: '💳',
      unlocked: credits > 0,
    },
    {
      id: 'assessed',
      title: 'Self-Aware',
      description: 'Complete your first assessment.',
      icon: '🧠',
      unlocked: false,
    },
  ];

  const unlockedCount = achievements.filter(a => a.unlocked).length;

  return (
    <div className="page">
      <AppBar title="Achievements" onBack={() => navigate(-1)} />

      <div style={{ flex: 1, overflowY: 'auto', padding: '20px 20px 40px' }}>
        {/* Progress header */}
        <FadeInAnimation delay={0}>
          <div style={{
            padding: '20px',
            background: 'var(--color-primary)',
            borderRadius: 20,
            marginBottom: 24,
            textAlign: 'center',
            color: 'white',
          }}>
            <div style={{ fontSize: 42, fontWeight: 800 }}>
              {unlockedCount}/{achievements.length}
            </div>
            <div style={{ fontSize: 14, opacity: 0.8, marginBottom: 16 }}>
              Achievements Unlocked
            </div>
            <div style={{
              height: 8, borderRadius: 99,
              background: 'rgba(255,255,255,0.2)',
              overflow: 'hidden',
            }}>
              <div style={{
                height: '100%',
                width: `${(unlockedCount / achievements.length) * 100}%`,
                background: 'white',
                borderRadius: 99,
                transition: 'width 1s ease',
              }} />
            </div>
          </div>
        </FadeInAnimation>

        {/* Stats */}
        <FadeInAnimation delay={0.1}>
          <div style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr 1fr',
            gap: 10,
            marginBottom: 24,
          }}>
            {[
              { label: 'Sessions', value: completedCount, icon: '📅' },
              { label: 'Credits', value: credits, icon: '💎' },
              { label: 'Streak', value: 0, icon: '🔥' },
            ].map(stat => (
              <div key={stat.label} className="card" style={{ textAlign: 'center', padding: '14px 8px' }}>
                <div style={{ fontSize: 24, marginBottom: 4 }}>{stat.icon}</div>
                <div style={{ fontSize: 20, fontWeight: 800, color: 'var(--color-primary)' }}>{stat.value}</div>
                <div style={{ fontSize: 11, color: 'var(--color-on-surface-variant)' }}>{stat.label}</div>
              </div>
            ))}
          </div>
        </FadeInAnimation>

        {/* Achievement list */}
        {loading ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {[0, 1, 2, 3].map(i => (
              <div key={i} className="card" style={{ display: 'flex', gap: 14 }}>
                <ShimmerAnimation width={52} height={52} borderRadius="12px" />
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 8 }}>
                  <ShimmerAnimation width="55%" height={15} />
                  <ShimmerAnimation width="80%" height={12} />
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {achievements.map((a, i) => (
              <FadeInAnimation key={a.id} delay={i * 0.07}>
                <div className="card" style={{
                  display: 'flex', gap: 14, alignItems: 'center',
                  opacity: a.unlocked ? 1 : 0.5,
                  transition: 'opacity 0.3s',
                }}>
                  <div style={{
                    width: 52, height: 52, borderRadius: 14,
                    background: a.unlocked ? 'var(--color-primary-container)' : 'var(--color-surface-variant)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: 26, flexShrink: 0,
                    filter: a.unlocked ? 'none' : 'grayscale(1)',
                  }}>
                    {a.icon}
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 700, fontSize: 14, color: 'var(--color-on-surface)', marginBottom: 3 }}>
                      {a.title}
                    </div>
                    <div style={{ fontSize: 12, color: 'var(--color-on-surface-variant)' }}>
                      {a.description}
                    </div>
                  </div>
                  {a.unlocked && (
                    <div style={{
                      width: 28, height: 28, borderRadius: '50%',
                      background: 'var(--color-success)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      flexShrink: 0,
                    }}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="20 6 9 17 4 12"/>
                      </svg>
                    </div>
                  )}
                </div>
              </FadeInAnimation>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AchievementPage;
